// ======================================================================
// File: FMSDashboard.jsx
// Enterprise Interactive Executive Dashboard (Hook Safe + Dark Mode)
// ======================================================================

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axiosClient from "../../../../api/axiosClient";
import {
  PieChart, Pie, Cell, Legend,
  LineChart, Line,
  XAxis, YAxis, Tooltip,
  CartesianGrid, ResponsiveContainer,
  BarChart, Bar
} from "recharts";

import "./FMSDashboard.css";

/* =========================================================
   DATE HELPER
========================================================= */
const getLast7Days = () => {
  const today = new Date();
  const lastWeek = new Date();
  lastWeek.setDate(today.getDate() - 7);

  return {
    start: lastWeek.toISOString().slice(0, 10),
    end: today.toISOString().slice(0, 10)
  };
};

/* =========================================================
   ANIMATED COUNTER HOOK (SAFE)
========================================================= */
const useAnimatedNumber = (value, duration = 800) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let start = 0;
    const increment = value / (duration / 16);

    const timer = setInterval(() => {
      start += increment;
      if (start >= value) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [value, duration]);

  return displayValue;
};

/* =========================================================
   KPI CARD COMPONENT (HOOK SAFE)
========================================================= */
function KPICard({ kpi, navigate }) {

  const animatedValue = useAnimatedNumber(kpi.value);

  const handleClick = () => {
    if (kpi.status === "OVERDUE") {
      navigate("/ppe/fms/followups?overdue=true");
    } else if (kpi.status === "ESCALATED") {
      navigate("/ppe/fms/followups?escalated=true");
    } else {
      navigate(`/ppe/fms/followups?status=${kpi.status}`);
    }
  };

  return (
    <div
      className={`kpi-card ${kpi.class}`}
      onClick={handleClick}
    >
      <div className="kpi-label">{kpi.label}</div>
      <div className="kpi-value">{animatedValue}</div>
    </div>
  );
}

/* =========================================================
   MAIN DASHBOARD COMPONENT
========================================================= */
export default function FMSDashboard() {

  const navigate = useNavigate();
  const defaultDates = getLast7Days();

  const [darkMode, setDarkMode] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    start_date: defaultDates.start,
    end_date: defaultDates.end,
    domain: ""
  });

  /* =========================================================
     LOAD DASHBOARD (useCallback FIX)
  ========================================================= */
  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);

      const res = await axiosClient.get(
        "/api/ppe/fms/dashboard/manager",
        {
          params: {
            start_date: filters.start_date,
            end_date: filters.end_date,
            domain: filters.domain
          }
        }
      );

      setData(res.data?.data || null);

    } catch (err) {
      console.error("Dashboard Load Error:", err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    document.body.classList.toggle("dark-mode", darkMode);
  }, [darkMode]);

  if (loading) return <div className="dashboard-loading">Loading Dashboard...</div>;
  if (!data) return null;

  const {
    summary = {},
    domain_distribution = [],
    trend_created = [],
    trend_completed = [],
    team_workload = [],
    aging_buckets = {},
    automation_health = {}
  } = data;

  /* =========================================================
     SAFE TREND MERGE BY DATE
  ========================================================= */
  const completedMap = {};
  trend_completed.forEach(item => {
    completedMap[item.date] = item.completed_count;
  });

  const trendMerged = trend_created.map(item => ({
    date: item.date,
    created_count: item.created_count,
    completed_count: completedMap[item.date] || 0
  }));

  /* =========================================================
     KPI SETUP
  ========================================================= */
  const kpis = [
    { label: "Open", value: summary.open || 0, status: "OPEN", class: "kpi-blue" },
    { label: "In Progress", value: summary.in_progress || 0, status: "IN_PROGRESS", class: "kpi-orange" },
    { label: "Completed", value: summary.completed || 0, status: "COMPLETED", class: "kpi-green" },
    { label: "Overdue", value: summary.overdue || 0, status: "OVERDUE", class: "kpi-red" },
    { label: "Escalated", value: summary.escalated || 0, status: "ESCALATED", class: "kpi-purple" },
    { label: "Recurring", value: summary.recurring || 0, status: "RECURRING", class: "kpi-teal" }
  ];

  const pieColors = ["#4f46e5", "#10b981", "#f59e0b", "#ef4444", "#9333ea"];

  return (
    <div className={`fms-dashboard ${darkMode ? "dark" : ""}`}>

      {/* HEADER */}
      <div className="dashboard-header glass-header">
        <h2>FMS Executive Dashboard</h2>

        <div className="dashboard-filters">
          <input
            type="date"
            value={filters.start_date}
            onChange={(e) =>
              setFilters({ ...filters, start_date: e.target.value })
            }
          />
          <input
            type="date"
            value={filters.end_date}
            onChange={(e) =>
              setFilters({ ...filters, end_date: e.target.value })
            }
          />
          <button onClick={loadDashboard}>Apply</button>

          <button
            className="theme-toggle"
            onClick={() => setDarkMode(!darkMode)}
          >
            {darkMode ? "☀ Light" : "🌙 Dark"}
          </button>
        </div>
      </div>

      {/* KPI CARDS */}
      <div className="kpi-row">
        {kpis.map((kpi, index) => (
          <KPICard
            key={index}
            kpi={kpi}
            navigate={navigate}
          />
        ))}
      </div>

      {/* DOMAIN + TREND */}
      <div className="chart-row">

        <div className="chart-card glass-card">
          <h5>Domain Distribution</h5>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={domain_distribution}
                dataKey="count"
                nameKey="domain_code"
                outerRadius={100}
              >
                {domain_distribution.map((entry, index) => (
                  <Cell
                    key={index}
                    fill={pieColors[index % pieColors.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card glass-card">
          <h5>Created vs Completed Trend</h5>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trendMerged}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="created_count"
                stroke="#4f46e5"
                strokeWidth={3}
              />
              <Line
                type="monotone"
                dataKey="completed_count"
                stroke="#10b981"
                strokeWidth={3}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

      </div>

      {/* TEAM WORKLOAD */}
      <div className="chart-card glass-card">
        <h5>Team Performance</h5>
        <table className="team-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Open</th>
              <th>In Progress</th>
              <th>Overdue</th>
            </tr>
          </thead>
          <tbody>
            {team_workload.map((u, i) => (
              <tr key={i}>
                <td>{u.assigned_to || "Unassigned"}</td>
                <td>{u.open_count || 0}</td>
                <td>{u.in_progress_count || 0}</td>
                <td className="overdue-cell">{u.overdue_count || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* AGING + AUTOMATION */}
      <div className="chart-row">

        <div className="chart-card glass-card">
          <h5>Aging Buckets</h5>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={[
                { name: "0-2 Days", value: aging_buckets.bucket_0_2 || 0 },
                { name: "3-5 Days", value: aging_buckets.bucket_3_5 || 0 },
                { name: "6-10 Days", value: aging_buckets.bucket_6_10 || 0 },
                { name: "10+ Days", value: aging_buckets.bucket_10_plus || 0 }
              ]}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="value" fill="#6366f1" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card glass-card automation-card">
          <h5>Automation Health</h5>
          <div>Emails Sent: {automation_health.emails_sent || 0}</div>
          <div>Failed Actions: {automation_health.failed_actions || 0}</div>
          <div>Pending Jobs: {automation_health.pending_jobs || 0}</div>
          <div>Retry Jobs: {automation_health.retry_jobs || 0}</div>
        </div>

      </div>

    </div>
  );
}
